/**
 * useTaskArchive - Archive and unarchive task operations
 * 
 * Handles:
 * - Manual archiving (any status)
 * - Unarchiving (restores to original status if parent campaign exists)
 * - Orphaned task handling (parent campaign deleted)
 */

export const useTaskArchive = (localSprint, setLocalSprint, recordAction) => {

  /**
   * Archive a task
   * Can be called from any status - stores original status for restoration
   */
  const handleArchiveTask = (taskId, reason = 'manual') => {
    const now = new Date().toISOString();
    
    // Get task name for undo description before archiving
    let taskName = '';
    setLocalSprint(prev => {
      const task = prev?.tasks?.find(t => t.id === taskId);
      if (task) taskName = task.name;
      return prev;
    });
    
    setLocalSprint(prev => {
      if (!prev) return prev;
      
      const tasks = prev.tasks || [];
      const taskToArchive = tasks.find(t => t.id === taskId);
      
      if (!taskToArchive) return prev;
      
      // Store the current state for restoration
      const updatedTasks = tasks.map(task => {
        if (task.id === taskId) {
          return {
            ...task,
            archived: true,
            archivedAt: now,
            archivedReason: reason,
            archivedFromStatus: task.status, // Store original status for restoration
            updatedAt: now,
          };
        }
        return task;
      });
      
      return { ...prev, tasks: updatedTasks };
    });

    // Record for undo
    if (recordAction && taskName) {
      recordAction({
        type: 'ARCHIVE_TASK',
        description: `Archive: "${taskName}"`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t =>
                t.id === taskId
                  ? { ...t, archived: false, archivedAt: null, archivedReason: null, archivedFromStatus: null }
                  : t
              ),
            };
          });
        },
        redo: () => {
          handleArchiveTask(taskId, reason);
        },
      });
    }
  };

  /**
   * Archive multiple tasks at once
   * Used for bulk operations
   */
  const handleArchiveTasks = (taskIds, reason = 'manual') => {
    const now = new Date().toISOString();
    
    setLocalSprint(prev => {
      if (!prev) return prev;
      
      const tasks = prev.tasks || [];
      const updatedTasks = tasks.map(task => {
        if (taskIds.includes(task.id)) {
          return {
            ...task,
            archived: true,
            archivedAt: now,
            archivedReason: reason,
            archivedFromStatus: task.status,
            updatedAt: now,
          };
        }
        return task;
      });
      
      return { ...prev, tasks: updatedTasks };
    });

    // Record for undo
    if (recordAction) {
      recordAction({
        type: 'ARCHIVE_TASKS',
        description: `Archive ${taskIds.length} task${taskIds.length > 1 ? 's' : ''}`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t =>
                taskIds.includes(t.id)
                  ? { ...t, archived: false, archivedAt: null, archivedReason: null, archivedFromStatus: null }
                  : t
              ),
            };
          });
        },
        redo: () => {
          handleArchiveTasks(taskIds, reason);
        },
      });
    }
  };

  /**
   * Unarchive a task
   * Restores to original status if parent campaign still exists
   * For orphaned tasks (parent deleted), caller handles special logic
   */
  const handleUnarchiveTask = (taskId) => {
    const now = new Date().toISOString();
    
    setLocalSprint(prev => {
      if (!prev) return prev;
      
      const tasks = prev.tasks || [];
      const taskToRestore = tasks.find(t => t.id === taskId);
      
      if (!taskToRestore || !taskToRestore.archived) return prev;
      
      const updatedTasks = tasks.map(task => {
        if (task.id === taskId) {
          // Restore to original status (or 'pending' as fallback)
          const restoredStatus = task.archivedFromStatus || 'pending';
          
          return {
            ...task,
            archived: false,
            archivedAt: null,
            archivedReason: null,
            archivedFromStatus: null,
            status: restoredStatus,
            updatedAt: now,
          };
        }
        return task;
      });
      
      return { ...prev, tasks: updatedTasks };
    });

    // Record for undo
    if (recordAction) {
      setLocalSprint(prev => {
        const task = prev?.tasks?.find(t => t.id === taskId);
        if (task) {
          recordAction({
            type: 'UNARCHIVE_TASK',
            description: `Restore: "${task.name}"`,
            undo: () => {
              handleArchiveTask(taskId, task.archivedReason || 'manual');
            },
            redo: () => {
              handleUnarchiveTask(taskId);
            },
          });
        }
        return prev;
      });
    }
  };

  /**
   * Auto-archive completed tasks with no updates in X days
   * Called on campaign load
   * 
   * Criteria:
   * - Task is in a status with category 'completed'
   * - Task has not been updated in the past X days (uses updatedAt)
   * - Task is not already archived
   * 
   * Using updatedAt (instead of completedAt) means:
   * - If someone edits a completed task, the clock resets
   * - Restoring from archive updates updatedAt, preventing immediate re-archive
   */
  const handleAutoArchiveCompletedTasks = (daysThreshold = 7) => {
    const now = new Date();
    const thresholdMs = daysThreshold * 24 * 60 * 60 * 1000;
    
    setLocalSprint(prev => {
      if (!prev) return prev;
      
      const tasks = prev.tasks || [];
      const statuses = prev.statuses || [];
      
      // Find all completed status IDs
      const completedStatusIds = statuses
        .filter(s => s.category === 'completed')
        .map(s => s.id);
      
      if (completedStatusIds.length === 0) return prev;
      
      const tasksToArchive = [];
      const updatedTasks = tasks.map(task => {
        // Skip if already archived
        if (task.archived) return task;
        
        // Check if task is in a completed status
        if (!completedStatusIds.includes(task.status)) return task;
        
        // Check if task has been inactive long enough (using updatedAt)
        // Fall back to completedAt if updatedAt doesn't exist (legacy data)
        const lastActivityDate = task.updatedAt || task.completedAt;
        if (!lastActivityDate) return task;
        
        const lastActivity = new Date(lastActivityDate);
        const timeSinceLastActivity = now - lastActivity;
        
        if (timeSinceLastActivity >= thresholdMs) {
          tasksToArchive.push(task.id);
          return {
            ...task,
            archived: true,
            archivedAt: now.toISOString(),
            archivedReason: 'auto',
            archivedFromStatus: task.status,
            updatedAt: now.toISOString(),
          };
        }
        
        return task;
      });
      
      // Only update if we actually archived something
      if (tasksToArchive.length > 0) {
        console.log(`[Auto-Archive] Archived ${tasksToArchive.length} completed task(s) with no activity in ${daysThreshold} days`);
        return { ...prev, tasks: updatedTasks };
      }
      
      return prev;
    });
  };

  return {
    handleArchiveTask,
    handleArchiveTasks,
    handleUnarchiveTask,
    handleAutoArchiveCompletedTasks,
  };
};

export default useTaskArchive;
